// useQuickSave — a save without a form (ADR-084 §1 · §6): a toggle, a per-control setting, a text saved on blur, a
// one-click act (a follow star, an Enable in a menu, a file's Restore). One value travels, so no stamp; the control is
// busy while the save is in flight; a failure is said ONCE in a keyed toast with the code's words (a busy answer was
// said by the door). The control springs back to the server's value through its hook's own re-read, run on settled
// (`refreshOnSettled`, lib/settled.ts) — one way, never a host's. Success is silent — the control's new state is
// the feedback; a host that announces one does it when `save` resolves true.
//
// `useRefusalToast` is its failure half on its own — for a screen whose several one-click acts (a library's Restore ·
// Undo · Download) share one way of failing: the same keyed toast, the same silence on a busy answer.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { useToast } from '../../ui/Toast.js';
import { apiErrorMessage } from '../../../lib/apiError.js';
import { isBusy } from '../../../lib/refusal.js';

export interface QuickSaveOptions {
  /** The toast's key — one per control, so a burst never stacks. */
  key: string;
}

export interface QuickSave<TVars> {
  /** Resolves true once saved, false on a refusal (said by then) — a host that announces a success does it on true. */
  save: (vars: TVars) => Promise<boolean>;
  busy: boolean;
}

/** Say a refused write without a form: ONE toast per key (a second replaces the first) in the code's words; nothing
 *  on a busy answer — the door said it. */
export function useRefusalToast(key: string): (err: unknown) => void {
  const toast = useToast();
  const { t: tErrors } = useTranslation('errors');
  return useCallback(
    (err: unknown) => {
      if (!isBusy(err)) toast.error(apiErrorMessage(err, tErrors), { key });
    },
    // useToast hands a fresh object each render; its `error` is the store's one `add`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tErrors, key],
  );
}

export function useQuickSave<TData, TVars>(
  mutation: Pick<UseMutationResult<TData, Error, TVars>, 'mutateAsync' | 'isPending'>,
  { key }: QuickSaveOptions,
): QuickSave<TVars> {
  const refused = useRefusalToast(key);
  const { mutateAsync } = mutation;
  const save = useCallback(
    async (vars: TVars) => {
      try {
        await mutateAsync(vars);
        return true;
      } catch (err) {
        refused(err);
        return false;
      }
    },
    [mutateAsync, refused],
  );
  return { save, busy: mutation.isPending };
}
